import React, { useCallback, useMemo, useState } from "react";
import { keyBy } from "@common/helpers/collections";
import { omit, omitBy } from "@common/helpers/objects";
import { ChildrenProps } from "../../../../types";
import { ClientDetailsRow } from "../views/clientViewConfig";
import {
    clientBulkSelectionContext,
    ClientBulkSelectionContextValue as ValueType,
} from "./clientBulkSelectionContext";

export const ClientBulkSelectionContextProvider: React.FC<ChildrenProps> = ({
    children,
}) => {
    const [selected, setSelected] = useState<
        Record<ClientDetailsRow["id"], ClientDetailsRow>
    >({});

    const selectedArray = useMemo(() => Object.values(selected), [selected]);

    const isSelected = useCallback(
        (client: ClientDetailsRow) => client.id in selected,
        [selected],
    );

    const select: ValueType["select"] = useCallback((clientOrClients) => {
        const selectionUpdate: Record<string, ClientDetailsRow> = Array.isArray(
            clientOrClients,
        )
            ? keyBy(clientOrClients, "id")
            : {
                  [clientOrClients.id]: clientOrClients,
              };

        setSelected((prev) => ({ ...prev, ...selectionUpdate }));
    }, []);

    const deselect: ValueType["deselect"] = useCallback((clientOrClients) => {
        if (Array.isArray(clientOrClients)) {
            const dict = keyBy(clientOrClients, "id");
            setSelected((prev) => omitBy(prev, (value) => value.id in dict));
        } else {
            setSelected((prev) => omit(prev, clientOrClients.id));
        }
    }, []);

    const clearSelection = useCallback(() => {
        setSelected({});
    }, []);

    const value: ValueType = useMemo(
        () => ({
            selected: selectedArray,
            selectedCount: selectedArray.length,
            select,
            deselect,
            clearSelection,
            isSelected,
        }),
        [clearSelection, deselect, isSelected, select, selectedArray],
    );

    return (
        <clientBulkSelectionContext.Provider value={value}>
            {children}
        </clientBulkSelectionContext.Provider>
    );
};
